import type {
  VendorCustomerActivitiesListApiArg,
  VendorSalesChannelsUpdateApiArg,
  VendorSalesChannelsUpdateApiResponse,
} from './generatedQueries/internalDashboard';
import { internalDashboardApi as generatedInternalDashboardApi } from './generatedQueries/internalDashboard';
import { createMutationHandler } from './utils/errorHandling';

type VendorSalesChannelsUpdateArg = VendorSalesChannelsUpdateApiArg & {
  skipSuccessToast?: boolean;
};

const vendorCustomersTag = (vendorId: number) => ({
  type: 'VendorCustomers' as const,
  id: vendorId,
});

const vendorLocationsTag = (vendorId: number) => ({
  type: 'VendorLocations' as const,
  id: vendorId,
});

const salesChannelsListTag = (vendorId: number) => ({
  type: 'SalesChannels' as const,
  id: `LIST-${vendorId}`,
});

const salesChannelItemTag = (channelId: number) => ({
  type: 'SalesChannels' as const,
  id: channelId,
});

const vendorCustomerClassesTag = (vendorId: number) => ({
  type: 'VendorCustomerClasses' as const,
  id: vendorId,
});

const vendorCustomerActivitiesTag = (vendorId: number, customerId: number) => ({
  type: 'VendorCustomerActivities' as const,
  id: `${vendorId}_${customerId}`,
});

const vendorCustomerTaxesFeesTag = (vendorId: number) => ({
  type: 'VendorCustomerTaxesFees' as const,
  id: vendorId,
});

const customerDetailsToast = { successAutoCloseTimeout: 1000 } as const;

function appendRepeatedParam(
  params: URLSearchParams,
  key: string,
  value: string | string[] | number | number[] | undefined
) {
  if (value === undefined || value === null || value === '') return;
  const values = Array.isArray(value) ? value : [value];
  values.forEach((v) => {
    if (v !== undefined && v !== null && v !== '') {
      params.append(key, String(v));
    }
  });
}

/** Supports repeated query params (activity_type, activity_status, user_id) like the legacy client. */
function buildVendorCustomerActivitiesListUrl(
  queryArg: VendorCustomerActivitiesListApiArg & {
    activityType?: string | string[];
    activityStatus?: string | string[];
    userId?: number | number[];
  }
) {
  const params = new URLSearchParams();
  if (queryArg.page != null) params.set('page', String(queryArg.page));
  if (queryArg.pageSize != null)
    params.set('page_size', String(queryArg.pageSize));
  if (queryArg.title) params.set('title', queryArg.title);
  if (queryArg.fromDate) params.set('from_date', queryArg.fromDate);
  if (queryArg.toDate) params.set('to_date', queryArg.toDate);

  appendRepeatedParam(params, 'activity_type', queryArg.activityType);
  appendRepeatedParam(params, 'activity_status', queryArg.activityStatus);
  appendRepeatedParam(params, 'user_id', queryArg.userId);

  return `/vendor/${queryArg.vendorId}/customer/${queryArg.customerId}/activities/?${params.toString()}`;
}

const generatedApi = generatedInternalDashboardApi.injectEndpoints({
  endpoints: (build) => ({
    vendorSalesChannelsUpdate: build.mutation<
      VendorSalesChannelsUpdateApiResponse,
      VendorSalesChannelsUpdateArg
    >({
      query: ({ vendorId, id, salesChannelRequestRequest }) => ({
        url: `/vendor/${vendorId}/sales-channels/${id}/`,
        method: 'PUT',
        body: salesChannelRequestRequest,
      }),
    }),
  }),
  overrideExisting: true,
});

export const internalDashboardApi = generatedApi.enhanceEndpoints({
  addTagTypes: [
    'VendorCustomers',
    'VendorLocations',
    'SalesChannels',
    'VendorCustomerClasses',
    'VendorCustomerActivities',
    'VendorCustomerTaxesFees',
    'VendorImportBatchesImportProductsList',
    'VendorImportBatchesImportStrainsList',
    'VendorImportBatchesImportVariantsList',
  ],
  endpoints: {
    vendorCustomersList: {
      providesTags: (_result, _error, arg) => [
        vendorCustomersTag(arg.vendorId),
      ],
    },
    vendorCustomersBulkUpdateCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomersTag(arg.vendorId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update customers',
        'Customers updated successfully'
      ),
    },
    vendorLocationsList: {
      providesTags: (_result, _error, arg) => [
        vendorLocationsTag(arg.vendorId),
      ],
    },
    vendorLocationsBulkUpdateCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorLocationsTag(arg.vendorId),
      ],
    },
    vendorCustomerLocationsPaymentTermsBulkUpdateCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorLocationsTag(arg.vendorId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update location payment terms',
        'Location payment terms updated successfully'
      ),
    },
    vendorCustomerLocationsBillingStatusBulkUpdateCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorLocationsTag(arg.vendorId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update location billing status',
        'Location billing status updated successfully'
      ),
    },
    vendorLocationsCustomerClassesBulkUpdateCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorLocationsTag(arg.vendorId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update location customer classes',
        'Location customer classes updated successfully'
      ),
    },
    vendorSalesChannelsList: {
      providesTags: (_result, _error, arg) => [
        salesChannelsListTag(arg.vendorId),
      ],
    },
    vendorSalesChannelsRetrieve: {
      providesTags: (_result, _error, arg) => [
        salesChannelsListTag(arg.vendorId),
        salesChannelItemTag(arg.id),
      ],
    },
    vendorSalesChannelsCreate: {
      invalidatesTags: (_result, _error, arg) => [
        salesChannelsListTag(arg.vendorId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to create sales channel',
        'Sales channel created successfully'
      ),
    },
    vendorSalesChannelsUpdate: {
      invalidatesTags: (_result, _error, arg) => [
        salesChannelsListTag(arg.vendorId),
        salesChannelItemTag(arg.id),
      ],
      onQueryStarted: async (arg, api) => {
        const run = arg.skipSuccessToast
          ? createMutationHandler('Failed to update sales channel')
          : createMutationHandler(
              'Failed to update sales channel',
              'Sales channel updated successfully'
            );
        return run(arg, api);
      },
    },
    vendorCustomersDetailsPartialUpdate: {
      onQueryStarted: createMutationHandler(
        'Failed to update customer settings',
        'Customer settings updated',
        customerDetailsToast
      ),
    },
    vendorCustomersLocationsSalesRepsPartialUpdate: {
      onQueryStarted: createMutationHandler(
        'Failed to update location sales reps',
        'Location sales reps updated',
        customerDetailsToast
      ),
    },
    vendorCustomerLocationsPaymentTermsUpdate: {
      onQueryStarted: createMutationHandler(
        'Failed to update location payment terms',
        'Location payment terms updated',
        customerDetailsToast
      ),
    },
    vendorCustomerLocationsBillingStatusUpdate: {
      onQueryStarted: createMutationHandler(
        'Failed to update location billing status',
        'Location billing status updated',
        customerDetailsToast
      ),
    },
    vendorCustomersContactsCreate: {
      onQueryStarted: createMutationHandler(
        'Failed to create contact',
        'Contact created',
        customerDetailsToast
      ),
    },
    vendorCustomersContactsUpdate: {
      onQueryStarted: createMutationHandler(
        'Failed to update contact',
        'Contact updated',
        customerDetailsToast
      ),
    },
    vendorCustomersContactsDestroy: {
      onQueryStarted: createMutationHandler(
        'Failed to delete contact',
        'Contact deleted',
        customerDetailsToast
      ),
    },
    vendorCustomersNotesCreate: {
      onQueryStarted: createMutationHandler(
        'Failed to create note',
        'Note created',
        customerDetailsToast
      ),
    },
    vendorCustomersNotesUpdate: {
      onQueryStarted: createMutationHandler(
        'Failed to update note',
        'Note updated',
        customerDetailsToast
      ),
    },
    vendorCustomersNotesDestroy: {
      onQueryStarted: createMutationHandler(
        'Failed to delete note',
        'Note deleted',
        customerDetailsToast
      ),
    },
    vendorCustomerActivitiesList: {
      query: (queryArg) => ({
        url: buildVendorCustomerActivitiesListUrl(
          queryArg as VendorCustomerActivitiesListApiArg & {
            activityType?: string | string[];
            activityStatus?: string | string[];
            userId?: number | number[];
          }
        ),
      }),
      providesTags: (_result, _error, arg) => [
        vendorCustomerActivitiesTag(arg.vendorId, arg.customerId),
      ],
    },
    vendorCustomerActivitiesRetrieve: {
      providesTags: (_result, _error, arg) => [
        vendorCustomerActivitiesTag(arg.vendorId, arg.customerId),
        { type: 'VendorCustomerActivities' as const, id: arg.id },
      ],
    },
    vendorCustomerActivitiesCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomerActivitiesTag(arg.vendorId, arg.customerId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to create activity',
        'Activity created',
        customerDetailsToast
      ),
    },
    vendorCustomerActivitiesUpdate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomerActivitiesTag(arg.vendorId, arg.customerId),
        { type: 'VendorCustomerActivities', id: arg.id },
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update activity',
        'Activity updated',
        customerDetailsToast
      ),
    },
    vendorCustomerActivitiesDestroy: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomerActivitiesTag(arg.vendorId, arg.customerId),
        { type: 'VendorCustomerActivities', id: arg.id },
      ],
      onQueryStarted: createMutationHandler(
        'Failed to delete activity',
        'Activity deleted',
        customerDetailsToast
      ),
    },
    vendorCustomerActivitiesExportCreate: {
      query: (queryArg) => ({
        url: `/vendor/${queryArg.vendorId}/customer/${queryArg.customerId}/activities/export/`,
        method: 'POST',
        body: queryArg.activityExportRequest,
        responseHandler: (response: Response) => response.blob(),
      }),
    },
    vendorCustomerClassesList: {
      providesTags: (_result, _error, arg) => [
        vendorCustomerClassesTag(arg.vendorId),
      ],
    },
    vendorCustomerClassesRetrieve: {
      providesTags: (_result, _error, arg) => [
        vendorCustomerClassesTag(arg.vendorId),
        { type: 'VendorCustomerClasses' as const, id: arg.id },
      ],
    },
    vendorCustomerClassesCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomerClassesTag(arg.vendorId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to create customer class',
        'Customer class created successfully'
      ),
    },
    vendorCustomerClassesUpdate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomerClassesTag(arg.vendorId),
        { type: 'VendorCustomerClasses', id: arg.id },
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update customer class',
        'Customer class updated successfully'
      ),
    },
    vendorCustomerClassesDestroy: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomerClassesTag(arg.vendorId),
        { type: 'VendorCustomerClasses', id: arg.id },
      ],
      onQueryStarted: createMutationHandler(
        'Failed to delete customer class',
        'Customer class deleted successfully'
      ),
    },
    vendorCustomerTaxesFeesList: {
      providesTags: (_result, _error, arg) => [
        vendorCustomerTaxesFeesTag(arg.vendorId),
      ],
    },
    vendorCustomerTaxesFeesBulkUpdateCreate: {
      invalidatesTags: (_result, _error, arg) => [
        vendorCustomerTaxesFeesTag(arg.vendorId),
      ],
      onQueryStarted: createMutationHandler(
        'Failed to update customer taxes and fees',
        'Customer taxes and fees updated successfully'
      ),
    },

    vendorImportBatchesImportProductsDeselectAllCreate: {
      invalidatesTags: [
        { id: `SYNCED_false`, type: 'VendorImportBatchesImportProductsList' },
      ],
    },
    vendorImportBatchesImportProductsList: {
      providesTags: (result, error, arg) => [
        { id: 'LIST', type: 'VendorImportBatchesImportProductsList' },
        {
          id: `SYNCED_${arg.synced}`,
          type: 'VendorImportBatchesImportProductsList',
        },
      ],
    },
    vendorImportBatchesImportProductsPartialUpdate: {
      async onQueryStarted(
        // @ts-expect-error : need to accept the query to be able to update the cache
        {
          importProductsQuery,
          id: importProductId,
          patchedImportProductPartialUpdateRequest: { importProduct: data },
        },
        { dispatch, queryFulfilled }
      ) {
        const result = dispatch(
          internalDashboardApi.util.updateQueryData(
            'vendorImportBatchesImportProductsList',
            importProductsQuery,
            (draft) => {
              const importProduct = draft.importProducts.find(
                ({ id }) => id === importProductId
              );
              Object.assign(importProduct, data);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          result.undo();
        }
      },
    },
    vendorImportBatchesImportProductsSelectAllCreate: {
      invalidatesTags: [
        { id: `SYNCED_false`, type: 'VendorImportBatchesImportProductsList' },
      ],
    },

    vendorImportBatchesImportStrainsDeselectAllCreate: {
      invalidatesTags: [
        { id: `SYNCED_false`, type: 'VendorImportBatchesImportStrainsList' },
      ],
    },
    vendorImportBatchesImportStrainsList: {
      providesTags: (result, error, arg) => [
        { id: 'LIST', type: 'VendorImportBatchesImportStrainsList' },
        {
          id: `SYNCED_${arg.synced}`,
          type: 'VendorImportBatchesImportStrainsList',
        },
      ],
    },
    vendorImportBatchesImportStrainsPartialUpdate: {
      async onQueryStarted(
        // @ts-expect-error : need to accept the query to be able to update the cache
        {
          importStrainsQuery,
          id: importStrainId,
          patchedImportStrainPartialUpdateRequest: { importStrain: data },
        },
        { dispatch, queryFulfilled }
      ) {
        const result = dispatch(
          internalDashboardApi.util.updateQueryData(
            'vendorImportBatchesImportStrainsList',
            importStrainsQuery,
            (draft) => {
              const importStrain = draft.importStrains.find(
                ({ id }) => id === importStrainId
              );
              Object.assign(importStrain, data);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          result.undo();
        }
      },
    },
    vendorImportBatchesImportStrainsSelectAllCreate: {
      invalidatesTags: [
        { id: `SYNCED_false`, type: 'VendorImportBatchesImportStrainsList' },
      ],
    },

    vendorImportBatchesImportVariantsDeselectAllCreate: {
      invalidatesTags: [
        { id: `SYNCED_false`, type: 'VendorImportBatchesImportVariantsList' },
      ],
    },
    vendorImportBatchesImportVariantsList: {
      providesTags: (result, error, arg) => [
        { id: 'LIST', type: 'VendorImportBatchesImportVariantsList' },
        {
          id: `SYNCED_${arg.synced}`,
          type: 'VendorImportBatchesImportVariantsList',
        },
      ],
    },
    vendorImportBatchesImportVariantsPartialUpdate: {
      async onQueryStarted(
        // @ts-expect-error : need to accept the query to be able to update the cache
        {
          importVariantsQuery,
          id: importVariantId,
          patchedImportVariantPartialUpdateRequest: { importVariant: data },
        },
        { dispatch, queryFulfilled }
      ) {
        const result = dispatch(
          internalDashboardApi.util.updateQueryData(
            'vendorImportBatchesImportVariantsList',
            importVariantsQuery,
            (draft) => {
              const importVariant = draft.importVariants.find(
                ({ id }) => id === importVariantId
              );
              Object.assign(importVariant, data);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          result.undo();
        }
      },
    },
    vendorImportBatchesImportVariantsSelectAllCreate: {
      invalidatesTags: [
        { id: `SYNCED_false`, type: 'VendorImportBatchesImportVariantsList' },
      ],
    },
  },
});

export const {
  useLazyVendorImportBatchesImportProductsValidateRetrieveQuery,
  useLazyVendorImportBatchesImportStrainsValidateRetrieveQuery,
  useLazyVendorImportBatchesImportVariantsValidateRetrieveQuery,
  useVendorCustomerActivitiesCreateMutation,
  useVendorCustomerActivitiesDestroyMutation,
  useVendorCustomerActivitiesExportCreateMutation,
  useVendorCustomerActivitiesListQuery,
  useVendorCustomerActivitiesRetrieveQuery,
  useVendorCustomerActivitiesUpdateMutation,
  useVendorCustomerClassesCreateMutation,
  useVendorCustomerClassesDestroyMutation,
  useVendorCustomerClassesListQuery,
  useVendorCustomerClassesRetrieveQuery,
  useVendorCustomerClassesUpdateMutation,
  useVendorCustomerLocationsBillingStatusBulkUpdateCreateMutation,
  useVendorCustomerLocationsPaymentTermsBulkUpdateCreateMutation,
  useVendorCustomerTaxesFeesBulkUpdateCreateMutation,
  useVendorCustomerTaxesFeesListQuery,
  useVendorCustomersBulkUpdateCreateMutation,
  useVendorCustomersContactsListQuery,
  useVendorCustomersDetailsRetrieveQuery,
  useVendorCustomersListQuery,
  useVendorImportBatchesImportProductsDeselectAllCreateMutation,
  useVendorImportBatchesImportProductsListQuery,
  useVendorImportBatchesImportProductsPartialUpdateMutation,
  useVendorImportBatchesImportProductsSelectAllCreateMutation,
  useVendorImportBatchesImportStrainsDeselectAllCreateMutation,
  useVendorImportBatchesImportStrainsListQuery,
  useVendorImportBatchesImportStrainsPartialUpdateMutation,
  useVendorImportBatchesImportStrainsSelectAllCreateMutation,
  useVendorImportBatchesImportVariantsDeselectAllCreateMutation,
  useVendorImportBatchesImportVariantsListQuery,
  useVendorImportBatchesImportVariantsPartialUpdateMutation,
  useVendorImportBatchesImportVariantsSelectAllCreateMutation,
  useVendorImportBatchesProcessCreateMutation,
  useVendorLocationsBulkUpdateCreateMutation,
  useVendorLocationsCustomerClassesBulkUpdateCreateMutation,
  useVendorLocationsListQuery,
  useVendorSalesChannelsCreateMutation,
  useVendorSalesChannelsListQuery,
  useVendorSalesChannelsRetrieveQuery,
  useVendorSalesChannelsUpdateMutation,
  useVendorVendorUsersListQuery,
} = internalDashboardApi;
